/**
 * The HELP form's words (D27) - what the wearer has to tell somebody to bring
 * them into this session: the join code and where to type it. Pure, so the
 * sentences are testable. Web port of Unity's `JoinCodeModel`.
 */

export type JoinCodeState =
  /** A live code to read out. */
  | 'ready'
  /** The deployment served a code but it has expired. */
  | 'expired'
  /** The config arrived with no code (hash-only deployment, or none minted). */
  | 'none'
  /** No config has been received yet. */
  | 'noConfig';

/** Parse the served expiry (ISO-8601) into epoch ms; null when absent/unparseable. */
export function parseExpiry(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt);
  return Number.isFinite(ms) ? ms : null;
}

export function classifyJoinCode(
  code: string,
  expiryMs: number | null,
  nowMs: number,
  configReceived: boolean,
): JoinCodeState {
  if (!configReceived) return 'noConfig';
  if (!code) return 'none';
  if (expiryMs !== null && nowMs >= expiryMs) return 'expired';
  return 'ready';
}

/** The console origin an assistant types, derived from the config endpoint actually in use. */
export function consoleOriginFromConfigUrl(configUrl: string): string {
  try {
    const url = new URL(configUrl);
    return `${url.protocol}//${url.host}`;
  } catch {
    return '';
  }
}

export function joinCodeFormTitle(state: JoinCodeState): string {
  switch (state) {
    case 'ready':
      return 'Get help';
    case 'expired':
      return 'Join code expired';
    case 'none':
      return 'No join code';
    default:
      return 'Not connected';
  }
}

/** Spaced digits read aloud more reliably than a run of six. */
function spaced(code: string): string {
  return code.split('').join(' ');
}

export function joinCodeFormBody(state: JoinCodeState, code: string, consoleOrigin: string): string {
  const where = consoleOrigin ? `Ask them to open ${consoleOrigin} and enter this code:` : 'Ask them to open the assist console and enter this code:';
  switch (state) {
    case 'ready':
      return `${where}\n\n${spaced(code)}`;
    case 'expired':
      return 'The join code this deployment served has expired. Ask an admin to rotate it, then press HELP again.';
    case 'none':
      return 'This deployment does not publish a join code. Ask an admin for the current code.';
    default:
      return 'The session is not connected yet, so there is no join code to show. Try again once the room is up.';
  }
}
